import React, { useCallback, useEffect, useRef, useState } from "react";
import "../styles/NewChatPage.css";

import CryptoManager from "../utils/cryptoManager";
import { editUsername } from "../api/userApi";
import { sendEncryptedMessage } from "../api/chatApi";
import { usePageActive } from "../utils/hooks";
import {
  loadSession,
  loadOtherSession,
  updateSessionUserName,
} from "../utils/sessionManager";

import EditableVisibleName from "../components/EditableVisibleName";
import ChatMessages from "../components/ChatMessages";
import ChatInput, { ChatInputHandle } from "../components/ChatInput";

import { useSessionExpiry } from "../utils/useSessionExpiry";
import { useMessageExpiry } from "../utils/useMessageExpiry";
import { useChatConnection } from "../utils/useChatConnection";
import { usePresenceManager } from "../utils/usePresenceManager";
import { useInputFocusManager } from "../utils/useInputFocusManager";

import { SHORT_MESSAGE_EXPIRY_MS } from "../constants/chat";
import { NewChatPageReadyProps } from "../api/types";
import {
  buildImportUserDeepLink,
  buildImportUserIntentLink,
  buildImportUserUniversalLink,
  getRouteIdentifier,
  getStoreLink,
  isAndroidDevice,
  isIOSDevice,
} from "../utils/chatPage";
import { getUserFacingErrorMessage } from "../utils/errorMessages";
import ChatMenu from "../components/ChatMenu";
import { useSessionBannerMenu } from "../context/SessionBannerMenuContext";
import SessionBurgerMenu from "../components/SessionBurgerMenu";

const PENDING_MOBILE_TRANSFER_KEY = "idlessPendingMobileTransfer";

const NewChatPageReady: React.FC<NewChatPageReadyProps> = ({
  mode,
  chatNumber,
  publicId,
  originalConnectValue,
}) => {
  const session = loadSession();
  const identifier = getRouteIdentifier(mode, chatNumber, publicId);

  const [currentConnectValue, setCurrentConnectValue] =
    useState(originalConnectValue);

  const isActive = usePageActive();
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const nameContainerRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);
  const chatInputRef = useRef<ChatInputHandle>(null);

  const [inputMessage, setInputMessage] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [showAppPrompt] = useState(true);
  const typingStopTimeoutRef = useRef<number | null>(null);
  const isSendingTypingRef = useRef(false);
  const hasUsedPageTapFocusRef = useRef(false);

  const [visibleName, setVisibleName] = useState(session.userName || "");
  const [draftName, setDraftName] = useState(session.userName || "");

  const [shortMessageExpiryMs, setShortMessageExpiryMs] = useState(
    SHORT_MESSAGE_EXPIRY_MS,
  );
  const [menuOpen, setMenuOpen] = useState(false);
  const [isInputFocused, setIsInputFocused] = useState(false);

  const { setMenu } = useSessionBannerMenu();

  useSessionExpiry();

  const { messages, setMessages, addMessage, shortenAllMessages } =
    useMessageExpiry(shortMessageExpiryMs);

  const handleReceiveMessage = useCallback(
    (plainText: string) => {
      addMessage({
        text: plainText,
        sender: "received",
        isPageActive: isActive,
      });
    },
    [addMessage, isActive],
  );

  const handleReceiveDecryptionFailure = useCallback(() => {
    addMessage({
      text: "[decryption failed]",
      sender: "received",
      isPageActive: isActive,
    });
  }, [addMessage, isActive]);

  const handleSeen = useCallback(() => {
    shortenAllMessages("sent");
  }, [shortenAllMessages]);

  const handleReceiveFetchedMessage = useCallback(
    ({
      id,
      plainText,
      sender,
      dateCreated,
      expiresAt,
      status,
    }: {
      id: string;
      plainText: string;
      sender: "sent" | "received";
      dateCreated?: string;
      expiresAt?: number;
      status?: "sent" | "delivered" | "failed";
    }) => {
      const alreadyExists = messages.some((message) => {
        return message.id === id;
      });

      if (alreadyExists) return;

      addMessage({
        id,
        text: plainText,
        sender,
        status,
        dateCreated,
        expiresAt,
        isPageActive: true,
      });
    },
    [messages, addMessage],
  );

  const { isChatReady, otherUserName, refreshMissedMessages } =
    useChatConnection({
      mode,
      identifier,
      chatNumber,
      publicId,
      myUserId: session.myUserId,
      requestToken: session.requestToken,
      isActive,
      onReceiveMessage: handleReceiveMessage,
      onReceiveFetchedMessage: handleReceiveFetchedMessage,
      onReceiveDecryptionFailure: handleReceiveDecryptionFailure,
      onSeen: handleSeen,
      onShortExpiryUpdated: setShortMessageExpiryMs,
      onPublicIdReplaced: setCurrentConnectValue,
    });

  const { otherUserInChat, otherUserTyping, sendRealtimeEvent } =
    usePresenceManager({
      myUserId: session.myUserId,
      requestToken: session.requestToken,
      identifier,
    });

  const { stopTypingAndMarkActive } = useInputFocusManager({
    chatInputRef,
    onTypingStop: () => forceStopTyping(),
    onChatActive: () => {},
  });

  useEffect(() => {
    const handlePageTap = (event: MouseEvent) => {
      if (hasUsedPageTapFocusRef.current || isInputFocused) return;

      const target = event.target as HTMLElement | null;
      if (!target) return;

      const clickedInChat = Boolean(target.closest(".chat-container"));
      const clickedControl = Boolean(
        target.closest(
          [
            "a",
            "button",
            "input",
            "textarea",
            "select",
            "label",
            "[role='button']",
            ".input-container",
            ".chat-menu-modal",
          ].join(","),
        ),
      );

      if (!clickedInChat || clickedControl) return;

      hasUsedPageTapFocusRef.current = true;
      chatInputRef.current?.focus();
    };

    document.addEventListener("click", handlePageTap, true);

    return () => {
      document.removeEventListener("click", handlePageTap, true);
    };
  }, [isInputFocused]);

  useEffect(() => {
    if (!isActive) return;

    shortenAllMessages("received");
  }, [isActive, shortenAllMessages]);

  // Auto-scroll to bottom when messages change
  useEffect(() => {
    const el = messagesContainerRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [messages]);

  // Refresh messages when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        refreshMissedMessages();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [refreshMissedMessages]);

  const forceStopTyping = useCallback(() => {
    if (typingStopTimeoutRef.current !== null) {
      window.clearTimeout(typingStopTimeoutRef.current);
      typingStopTimeoutRef.current = null;
    }

    isSendingTypingRef.current = false;

    sendRealtimeEvent("typing_stop");
    sendRealtimeEvent("chat_active");
  }, [sendRealtimeEvent]);

  const handleSendMessage = async () => {
    const trimmedMessage = inputMessage.trim();

    if (!trimmedMessage || !session.requestToken || !isChatReady) return;

    const otherSession = loadOtherSession();
    const otherUserId = otherSession?.otherUserId;
    const chatId = otherSession?.chatId;

    if (!otherUserId || !chatId) return;

    forceStopTyping();

    const optimisticText = trimmedMessage;
    const messageId = crypto.randomUUID();
    const dateCreated = String(Date.now());

    setInputMessage("");

    /**
     * Add immediately, before encryption/API.
     * This keeps A, B, C, D in the order the user sent them,
     * regardless of network lag.
     */
    addMessage({
      id: messageId,
      text: optimisticText,
      sender: "sent",
      status: "sent",
      dateCreated,
      isPageActive: isActive,
    });

    try {
      const myPrivateKey = await CryptoManager.getStoredPrivateKeyBase64();
      if (!myPrivateKey) throw new Error("No private key");

      const recipientPublicBase64 = sessionStorage.getItem("otherUserPubKey");
      if (!recipientPublicBase64) throw new Error("No public key");

      const encryptedBase64 = await CryptoManager.encryptWithECDH(
        myPrivateKey,
        recipientPublicBase64,
        optimisticText,
      );

      await sendEncryptedMessage(session.requestToken, {
        id: messageId,
        targetId: otherUserId,
        chatId,
        targetType: "Chat",
        text: optimisticText,
        content: encryptedBase64,
        attachmentUrl: null,
        attachmentType: null,
        dateCreated,
      });

      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId
            ? { ...message, status: "delivered" }
            : message,
        ),
      );
    } catch (error) {
      console.error(error);

      setMessages((prev) =>
        prev.map((message) =>
          message.id === messageId ? { ...message, status: "failed" } : message,
        ),
      );

      alert(getUserFacingErrorMessage(error, "Error sending message"));
    }
  };

  const handleSaveName = useCallback(async () => {
    try {
      if (!session.requestToken) return;

      await editUsername(draftName, session.requestToken);

      setVisibleName(draftName);
      setEditingName(false);
      sessionStorage.setItem("userName", draftName);
      updateSessionUserName(draftName);
    } catch (error: any) {
      console.error(error);
      alert(getUserFacingErrorMessage(error, "Failed to update username."));
    }
  }, [draftName, session.requestToken]);

  const handleOpenApp = useCallback(() => {
    if (!session.requestToken || !currentConnectValue) return;

    const userAgent = navigator.userAgent;
    const isIOS = isIOSDevice(userAgent);
    const isAndroid = isAndroidDevice(userAgent);

    // iOS (universal link) and Android (intent://) pick app-or-store
    // themselves; other browsers fall back to the store after a delay.
    const link = isIOS
      ? buildImportUserUniversalLink(
          session.requestToken,
          currentConnectValue,
          mode,
        )
      : isAndroid
        ? buildImportUserIntentLink(
            session.requestToken,
            currentConnectValue,
            mode,
          )
        : buildImportUserDeepLink(
            session.requestToken,
            currentConnectValue,
            mode,
          );

    let fellBackToStore = false;
    let fallbackTimer: number | null = null;

    if (!isIOS && !isAndroid) {
      fallbackTimer = window.setTimeout(() => {
        fallbackTimer = null;
        fellBackToStore = true;
        window.location.href = getStoreLink(userAgent);
      }, 5000);
    }

    // Only a hint: useChatConnection confirms the transfer with the backend.
    const markPendingMobileTransfer = () => {
      if (fellBackToStore) return;

      if (fallbackTimer !== null) {
        window.clearTimeout(fallbackTimer);
        fallbackTimer = null;
      }

      const otherSession = loadOtherSession();

      sessionStorage.setItem(
        PENDING_MOBILE_TRANSFER_KEY,
        JSON.stringify({
          chatId: otherSession?.chatId ?? null,
          startedAt: Date.now(),
        }),
      );
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        markPendingMobileTransfer();
      }
    };

    window.addEventListener("pagehide", markPendingMobileTransfer, {
      once: true,
    });
    document.addEventListener("visibilitychange", handleVisibilityChange, {
      once: true,
    });

    window.location.href = link;
  }, [session.requestToken, currentConnectValue, mode]);

  const handleOpenStore = useCallback(() => {
    const storeLink = getStoreLink(navigator.userAgent);
    window.open(storeLink, "_blank");
  }, []);

  const handleStartEditingName = useCallback(() => {
    setDraftName(visibleName);
    setEditingName(true);
  }, [visibleName]);

  const handleCancelEditingName = useCallback(() => {
    setDraftName(visibleName);
    setEditingName(false);
  }, [visibleName]);

  const handleOpenMenu = useCallback(() => {
    setMenuOpen(true);
  }, []);

  const handleCloseMenu = useCallback(() => {
    handleCancelEditingName();
    setMenuOpen(false);
  }, [handleCancelEditingName]);

  // Set session burger menu
  useEffect(() => {
    if (!session.userName || !session.requestToken) {
      setMenu(null);
      return;
    }

    setMenu(
      <div className="session-banner-actions">
        <button
          className="session-open-button"
          type="button"
          onClick={handleOpenApp}
        >
          Open App
        </button>
        <SessionBurgerMenu onOpen={handleOpenMenu} />
      </div>,
    );
  }, [
    setMenu,
    session.userName,
    session.requestToken,
    handleOpenMenu,
    handleOpenApp,
  ]);

  // Cleanup menu on unmount
  useEffect(() => {
    return () => {
      setMenu(null);
    };
  }, [setMenu]);

  // const forceStopTyping = useCallback(() => {
  //   if (typingStopTimeoutRef.current !== null) {
  //     window.clearTimeout(typingStopTimeoutRef.current);
  //     typingStopTimeoutRef.current = null;
  //   }

  //   isSendingTypingRef.current = false;

  //   sendRealtimeEvent("typing_stop");
  //   sendRealtimeEvent("chat_active");
  // }, [sendRealtimeEvent]);

  const handleUserTyped = useCallback(() => {
    if (!isChatReady) return;

    isSendingTypingRef.current = true;

    // Send typing_start on every real text edit.
    // This keeps remote typing alive while the user is typing.
    sendRealtimeEvent("typing_start");

    if (typingStopTimeoutRef.current !== null) {
      window.clearTimeout(typingStopTimeoutRef.current);
    }

    typingStopTimeoutRef.current = window.setTimeout(() => {
      isSendingTypingRef.current = false;
      typingStopTimeoutRef.current = null;

      sendRealtimeEvent("typing_stop");
      sendRealtimeEvent("chat_active");
    }, 3_000);
  }, [isChatReady, sendRealtimeEvent]);

  useEffect(() => {
    return () => {
      if (typingStopTimeoutRef.current !== null) {
        window.clearTimeout(typingStopTimeoutRef.current);
        typingStopTimeoutRef.current = null;
      }

      isSendingTypingRef.current = false;
    };
  }, []);

  return (
    <div className="chat-container">
      <h2 className="chat-header">
        <span className="chat-title">
          Chat with{" "}
          <span className="chat-username">{otherUserName || "..."}</span>
        </span>

        {otherUserInChat && (
          <span
            className="chat-presence-dot online"
            title="In chat"
            aria-label="In chat"
          />
        )}
      </h2>

      <ChatMessages
        messages={messages}
        messagesEndRef={messagesEndRef}
        otherUserTyping={otherUserTyping}
        myName={visibleName}
        otherName={otherUserName}
      />

      <ChatInput
        ref={chatInputRef}
        value={inputMessage}
        isChatReady={isChatReady}
        onChange={(value) => {
          setInputMessage(value);
          handleUserTyped();
        }}
        onSend={handleSendMessage}
        onFocus={() => {
          setIsInputFocused(true);
          sendRealtimeEvent("chat_active");
        }}
        onBlur={() => {
          setIsInputFocused(false);
          stopTypingAndMarkActive();
        }}
      />

      <ChatMenu
        isOpen={menuOpen}
        onClose={handleCloseMenu}
        editingName={editingName}
        visibleName={visibleName}
        draftName={draftName}
        nameContainerRef={nameContainerRef}
        onDraftNameChange={setDraftName}
        onStartEditing={handleStartEditingName}
        onSave={handleSaveName}
        onCancelEditing={handleCancelEditingName}
        onOpenApp={handleOpenApp}
        onOpenStore={handleOpenStore}
      />
    </div>
  );
};

export default NewChatPageReady;
